import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import MdTiptap from "components/MdTiptap"; // Updated import

import useAdminService from "../hooks/useAdminService";

function KnowledgeDocumentForm({ knowledgeDocument }) {
  const { createOrUpdateKnowledgeDocument, getAllKnowledgeDocumentSummaries } = useAdminService();
  const navigate = useNavigate();
  const [knowledgeDocumentLabels, setKnowledgeDocumentLabels] = useState([]);

  const [title, setTitle] = useState(knowledgeDocument?.subject || "");
  const [titleFormatted, setTitleFormatted] = useState(
    knowledgeDocument?.subjectFormatted || "[...]"
  );
  const [duplicateTitle, setDuplicateTitle] = useState(false);
  const [description, setDescription] = useState(knowledgeDocument?.summary || "");
  const [content, setContent] = useState(knowledgeDocument?.content || "");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const SNACKBAR_DURATION = 6000;
  const [openAlert, setOpenAlert] = useState(false);

  const [dirty, setDirty] = useState(false);

  /* ---------- helpers ---------- */
  const showAlert = (severity, message) => {
    if (severity === "error") setError(message);
    if (severity === "success") setSuccess(message);
    setOpenAlert(true);
  };

  const handleCloseAlert = () => setOpenAlert(false);

  /* ---------- handlers ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // basic front-end validation
    if (!title) {
      showAlert("error", "Titel mag niet leeg zijn.");
      return;
    }

    const documentData = {
      id: knowledgeDocument?.id,
      subject: title,
      summary: description,
      content: content,
    };

    const response = await createOrUpdateKnowledgeDocument(documentData);

    if (!response.success) {
      showAlert("error", response.error || "Onbekende fout bij opslaan.");
      return;
    }

    // success path
    setDirty(false);
    if (knowledgeDocument?.id) {
      showAlert("success", "Document bijgewerkt.");
    } else {
      showAlert("success", "Document opgeslagen.");
      // Give the user one second to read, then go to the fresh URL
      setTimeout(() => {
        navigate(`/admin/agents/knowledge-document?knowledgeDocumentId=${response.id}`, {
          replace: true,
        });
      }, 1000);
    }
  };

  const handleCancel = () => {
    if (dirty) {
      const confirm = window.confirm(
        "Weet je zeker dat je wilt annuleren? Al je wijzigingen zullen verloren gaan."
      );
      if (confirm) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleTitleChange = (e) => {
    setDirty(true);
    const rawTitle = e.target.value;
    setTitle(rawTitle);
    const stripped = rawTitle.toUpperCase().replace(/[^A-Z0-9]/g, "");

    const formattedTitle = stripped ? `[${stripped}]` : "[...]";
    setTitleFormatted(formattedTitle);
    const isDuplicate = knowledgeDocumentLabels.includes(formattedTitle);
    setDuplicateTitle(isDuplicate);
    console.log(isDuplicate, knowledgeDocumentLabels); // Log the actual value, not the state
  };

  const handleDescriptionChange = (e) => {
    setDirty(true);
    setDescription(e.target.value);
  };

  const handleContentChange = (val) => {
    setDirty(true);
    if (val.includes("[") || val.includes("]")) {
      showAlert("error", "* Het gebruik van rechte haakjes is niet toegestaan.");
    } else {
      setError("");
    }
    setContent(val);
  };

  useEffect(() => {
    const fetchKnowledgeDocumentLabels = async () => {
      const response = await getAllKnowledgeDocumentSummaries();
      if (response.error) {
        showAlert("error", response.error);
        return;
      }
      const labels = response.knowledgeDocumentSummaries
        .filter((summary) => summary.id !== knowledgeDocument?.id)
        .map((summary) => summary.label);
      setKnowledgeDocumentLabels(labels);
    };
    fetchKnowledgeDocumentLabels();
  }, [knowledgeDocument?.id]);

  /* ---------- render ---------- */
  return (
    <>
      <MKTypography variant="h6">Titel</MKTypography>
      <MKTypography variant="body2" fontWeight="regular" color="text">
        De naam waaronder dit document op het admin dashboard verschijnt. Daarnaast wordt de titel
        gebruikt om naar dit document te linken in de instructies van een agent. Als je in de
        instructies van een agent het label <strong>{titleFormatted}</strong> schrijft, wordt dat
        bij het uitvoeren van de instructies vervangen door de inhoud van dit kennisdocument.{" "}
        <strong>Let op:</strong> als je de titel van een kennisdocument aanpast, wordt dit niet
        automatisch gewijzigd in de instructies van de agents; dat moet je handmatig doen.
      </MKTypography>
      <MKTypography variant="h6" sx={{ mt: 4 }}>
        Beschrijving
      </MKTypography>
      <MKTypography variant="body2" fontWeight="regular" color="text">
        Een korte beschrijving van het kennisdocument. Dit wordt alleen gebruikt om in het admin
        dashboard aan te geven waar ieder document voor dient. Maar het heeft geen invloed op de
        werking van de chatbot.
      </MKTypography>
      <MKTypography variant="h6" sx={{ mt: 4 }}>
        Inhoud
      </MKTypography>
      <MKTypography variant="body2" fontWeight="regular" color="text">
        De inhoud van het kennisdocument. Dit is de kennisbasis waaruit de chatbot antwoorden kan
        geven op vragen van de gebruiker. Het gebruik van rechte haakjes <strong>[</strong> en{" "}
        <strong>]</strong> is niet toegestaan.
      </MKTypography>

      <Card sx={{ width: "100%", mt: 4 }}>
        <MKBox component="form" autoComplete="off" onSubmit={handleSubmit} p={3} width="100%">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MKInput
                variant="standard"
                label="Titel"
                fullWidth
                value={title}
                onChange={handleTitleChange}
                error={duplicateTitle}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MKTypography
                variant="body2"
                fontWeight="regular"
                color="text"
                sx={{ textAlign: "right" }}
              >
                <strong>{titleFormatted}</strong>
              </MKTypography>
            </Grid>

            <Grid item xs={12}>
              <MKInput
                variant="standard"
                label="Korte beschrijving"
                fullWidth
                value={description}
                onChange={handleDescriptionChange}
              />
            </Grid>

            <Grid item xs={12}>
              <MdTiptap
                value={content}
                onChange={handleContentChange}
                error={error}
                outputFormat="html" // You can change this to "markdown" if needed
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MKButton
                type="button"
                variant="outlined"
                color="dark"
                fullWidth
                onClick={handleCancel}
              >
                Annuleren
              </MKButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <MKButton
                type="submit"
                variant="gradient"
                color="dark"
                fullWidth
                disabled={!!error || !title || duplicateTitle}
              >
                Opslaan
              </MKButton>
            </Grid>
          </Grid>
        </MKBox>
      </Card>

      {/* Snackbar-based feedback */}
      <Snackbar open={openAlert} autoHideDuration={SNACKBAR_DURATION} onClose={handleCloseAlert}>
        <Alert
          onClose={handleCloseAlert}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </>
  );
}

KnowledgeDocumentForm.propTypes = {
  knowledgeDocument: PropTypes.object,
};

export default KnowledgeDocumentForm;
